import { useNavigate, Link } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { SkipLink, MAIN_CONTENT_ID } from './SkipLink.tsx';

/**
 * The front door — shown to every signed-out visitor before the
 * login/register forms, not just a bare "Sign in" panel. T-088 made its
 * primary action entering an FPL team id, not registering: submitting the
 * form below sends a visitor straight to `/try/$entryId`, the free,
 * signed-out squad-and-verdict read (see `PublicVerdict`) — the product
 * arguing for itself before it asks for anything. `onGetStarted`/`onSignIn`
 * still hand back to LoginGate's own mode switch for the visitors who
 * already know they want an account; this component owns no auth state.
 *
 * Also links to `/fixtures` (T-049) — the other surface a first-time
 * visitor can reach without signing up at all.
 */
interface LandingPageProps {
  onGetStarted: () => void;
  onSignIn: () => void;
}

const POSITION_LABELS: Record<string, string> = {
  GKP: 'Goalkeeper',
  DEF: 'Defender',
  MID: 'Midfielder',
  FWD: 'Forward',
};

/** Illustrative only — a plausible-looking XI to show the pitch view doing
 * its job, not a claim about any real manager's actual squad. Real player
 * names (the same convention the app's own test fixtures already use)
 * because a landing page of placeholder "Player 1" cards would look fake in
 * a way real names don't — this is obviously sample data on a marketing
 * page, not a fabricated result. Checked against this app's own live
 * /api/players/search (i.e. the real, current FPL data it ingests) when
 * last updated — a departed or transferred player here would undercut the
 * "real prices, form and fixtures" pitch immediately below it. */
const SAMPLE_PITCH: Array<{ position: keyof typeof POSITION_LABELS; players: Array<{ name: string; club: string; tag?: 'C' | 'V' }> }> = [
  { position: 'GKP', players: [{ name: 'Raya', club: 'ARS' }] },
  {
    position: 'DEF',
    players: [
      { name: 'Gabriel', club: 'ARS' },
      { name: 'Virgil', club: 'LIV' },
      { name: 'Guéhi', club: 'MCI' },
    ],
  },
  {
    position: 'MID',
    players: [
      { name: 'B.Fernandes', club: 'MUN', tag: 'C' },
      { name: 'Palmer', club: 'CHE' },
      { name: 'Saka', club: 'ARS' },
    ],
  },
  {
    position: 'FWD',
    players: [
      { name: 'Haaland', club: 'MCI', tag: 'V' },
      { name: 'Watkins', club: 'AVL' },
    ],
  },
];

const STEPS = [
  {
    title: 'Enter your team id',
    body: 'No screenshot, no CSV, no account — just the number from your own FPL points page. We pull this gameweek’s picks straight from FPL and score them, free.',
  },
  {
    title: 'Real prices, form and fixtures',
    body: 'Every player is matched against live FPL data — current price, recent minutes, upcoming opponents by name, not a blended difficulty score. Squad legality and points math are computed, never guessed.',
  },
  {
    title: 'A straight verdict',
    body: 'Captain, the transfer worth making — and the one that isn’t — with reasoning shown. "Hold" is a legitimate answer when it’s the right one.',
  },
];

export function LandingPage({ onGetStarted, onSignIn }: LandingPageProps) {
  const navigate = useNavigate();
  const [entryIdInput, setEntryIdInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = entryIdInput.trim();
    const entryId = Number(trimmed);
    if (!Number.isInteger(entryId) || entryId <= 0) {
      setError('That doesn’t look like an FPL team id — it should be a whole number.');
      return;
    }
    setError(null);
    void navigate({ to: '/try/$entryId', params: { entryId: trimmed } });
  }

  return (
    <div className="shell">
      <SkipLink />

      <header className="masthead">
        <h1 className="wordmark">
          The <em>Gaffer</em>
        </h1>
        <button type="button" className="ghost" onClick={onSignIn}>
          Sign in
        </button>
      </header>

      <main id={MAIN_CONTENT_ID} tabIndex={-1}>
        <section className="landing-hero" aria-labelledby="landing-heading">
          <div className="landing-hero-copy">
            <h2 id="landing-heading">
              Squad in. <em>Verdict out.</em>
            </h2>
            <p>
              Enter your Fantasy Premier League team id and see this gameweek's squad scored,
              flagged and checked against real prices and fixtures — free, before you create
              anything. Ask for the full verdict — captain, transfers, chips, reasoning shown —
              once you've seen it's worth asking for.
            </p>

            <form className="row row--flush" onSubmit={onSubmit}>
              <div className="field">
                <label htmlFor="landing-entry-id">FPL team id</label>
                <input
                  id="landing-entry-id"
                  inputMode="numeric"
                  value={entryIdInput}
                  onChange={(e) => setEntryIdInput(e.target.value)}
                  placeholder="3294828"
                />
              </div>
              <button type="submit">See my squad</button>
            </form>
            <p className="hint">
              It&rsquo;s the number in the address bar on your own FPL points page &mdash;
              <code>
                {' '}
                fantasy.premierleague.com/entry/<strong>3294828</strong>/event/1
              </code>
              .
            </p>
            {error && (
              <ul className="notices notices--flush">
                <li className="error">{error}</li>
              </ul>
            )}

            <p>
              Not ready to look up a team? <Link to="/fixtures">See the free fixture difficulty grid</Link> first.
            </p>
            <p>
              Don&rsquo;t take our word for it &mdash;{' '}
              <Link to="/record-public">see the public record</Link> of every captain call and transfer
              this tool has recommended, scored once its gameweek is played, misses included.
            </p>
          </div>

          <div className="landing-hero-visual" aria-hidden="true">
            <div className="pitch">
              {SAMPLE_PITCH.map((row) => (
                <div key={row.position} className="pitch-row">
                  {row.players.map((player) => (
                    <div key={player.name} className="pitch-card" data-club={player.club}>
                      <div className="name">
                        {player.name}
                        {player.tag === 'C' && <span className="pill pill--captain">C</span>}
                        {player.tag === 'V' && <span className="pill pill--vice">V</span>}
                      </div>
                      <div className="meta">{player.club}</div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="landing-steps" aria-labelledby="steps-heading">
          <h3 id="steps-heading" className="landing-steps-heading">
            How it works
          </h3>
          <div className="landing-step-grid">
            {STEPS.map((step, i) => (
              <div className="landing-step" key={step.title}>
                <span className="landing-step-number">{i + 1}</span>
                <h4>{step.title}</h4>
                <p>{step.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="panel panel--accent landing-cta" aria-labelledby="cta-heading">
          <h3 id="cta-heading">Free to look, an account to keep it</h3>
          <p className="hint">
            Create a free account to save your squad, get a weekly briefing before your deadline,
            and see whether the verdict was actually right — none of which the free read above can
            do on its own.
          </p>
          <button type="button" onClick={onGetStarted}>
            Get started free
          </button>
        </section>
      </main>

      <footer>
        Not affiliated with the Premier League or Fantasy Premier League. Advice is generated and
        can be wrong — you are still the manager.
      </footer>
    </div>
  );
}
